import { Link, useNavigate } from "react-router-dom";
import {
  Zap,
  LogOut,
  LayoutGrid,
  User,
  Ticket,
  Hash,
  ChevronDown,
  Shield,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import InstallPwaBanner from "./InstallPwaBanner";
import LegalFooter from "./LegalFooter";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = !!user && user.role === "admin";
  const isAuthed = !!user && user !== false;

  const displayName =
    (user && (user.name || (user.email && user.email.split("@")[0]))) || "";
  const avatar = user?.picture || null;

  // Generic purchase entry: redirect to the buy page where the 3 packs are listed.
  // Stripe is launched from there (with pack_type) once the user picked a pack.
  const buyGenericCode = () => {
    navigate("/acheter");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen relative isolate">
      {/* Global OTRA brand watermark — fixed to the viewport, centered, behind
          all content. */}
      <div
        data-testid="global-brand-watermark"
        aria-hidden="true"
        className="fixed inset-0 pointer-events-none flex items-center justify-center z-[-1]"
      >
        <div
          className="w-[80vmin] h-[80vmin] max-w-[760px] max-h-[760px]"
          style={{
            backgroundImage: 'url("/assets/otra-logo.png")',
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            opacity: 0.22,
            filter:
              "drop-shadow(0 0 45px rgba(0, 229, 255, 0.55)) drop-shadow(0 0 15px rgba(0, 229, 255, 0.4))",
          }}
        />
      </div>

      <header className="sticky top-0 z-30 panel border-b border-white/5">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2 px-3 sm:px-6 py-3 sm:py-4">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 sm:gap-3 group shrink-0"
            data-testid="header-logo"
          >
            <div className="relative">
              <Zap className="w-6 h-6 sm:w-7 sm:h-7 text-cyan-400" strokeWidth={2.5} />
              <div className="absolute inset-0 blur-md text-cyan-400 opacity-50">
                <Zap className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2.5} />
              </div>
            </div>
            <span className="font-display font-black uppercase tracking-tighter text-base sm:text-lg">
              Otra<span className="text-cyan-400 neon-text-primary">Quizz</span>
            </span>
          </Link>

          {/* Right cluster: Buy CTA + (user menu | login) */}
          <nav className="flex items-center gap-2 text-sm shrink-0">
            <button
              data-testid="nav-buy-code-button"
              onClick={buyGenericCode}
              className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-full bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-display font-bold uppercase text-[11px] sm:text-xs tracking-wider active:scale-95 transition-all"
            >
              <Ticket className="w-4 h-4" />
              <span className="sm:hidden">Acheter</span>
              <span className="hidden sm:inline">Acheter un code</span>
            </button>

            {isAuthed ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-user-menu-trigger"
                    className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-cyan-400/40 active:scale-95 transition-all"
                    title={user.email}
                  >
                    {avatar ? (
                      <img
                        src={avatar}
                        alt=""
                        data-testid="nav-user-avatar"
                        className="w-6 h-6 rounded-full object-cover shrink-0"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <span className="w-6 h-6 rounded-full bg-cyan-400/15 border border-cyan-400/40 flex items-center justify-center shrink-0">
                        <User className="w-3.5 h-3.5 text-cyan-300" />
                      </span>
                    )}
                    <span
                      className="hidden sm:inline max-w-[120px] truncate text-white/85"
                      data-testid="nav-user-name"
                    >
                      {displayName}
                    </span>
                    <ChevronDown className="w-4 h-4 text-white/60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  sideOffset={8}
                  data-testid="nav-user-menu-content"
                  className="w-60 bg-[#0a0a16]/95 backdrop-blur-xl border border-white/10 text-white"
                >
                  <DropdownMenuLabel className="flex items-center gap-2 px-3 py-2.5">
                    {avatar ? (
                      <img
                        src={avatar}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <span className="w-8 h-8 rounded-full bg-cyan-400/15 border border-cyan-400/40 flex items-center justify-center">
                        <User className="w-4 h-4 text-cyan-300" />
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate" data-testid="menu-user-name">
                        {displayName}
                      </div>
                      <div className="text-[11px] text-white/50 truncate">{user.email}</div>
                    </div>
                    {isAdmin && (
                      <span
                        data-testid="menu-admin-badge"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-mono-stat bg-cyan-400/15 text-cyan-300 border border-cyan-400/40"
                      >
                        <Shield className="w-2.5 h-2.5" />
                        Admin
                      </span>
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem
                    data-testid="nav-play-link"
                    onSelect={() => navigate("/play")}
                    className="cursor-pointer px-3 py-2.5 focus:bg-cyan-400/10 focus:text-cyan-200"
                  >
                    <Hash className="w-4 h-4 mr-2 text-cyan-300" />
                    J&apos;ai un code
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="nav-mycodes-link"
                    onSelect={() => navigate("/mes-codes")}
                    className="cursor-pointer px-3 py-2.5 focus:bg-fuchsia-400/10 focus:text-fuchsia-200"
                  >
                    <Ticket className="w-4 h-4 mr-2 text-fuchsia-300" />
                    Mes codes
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator className="bg-white/10" />
                      <DropdownMenuItem
                        data-testid="nav-admin-button"
                        onSelect={() => navigate("/admin")}
                        className="cursor-pointer px-3 py-2.5 focus:bg-cyan-400/10 focus:text-cyan-200"
                      >
                        <LayoutGrid className="w-4 h-4 mr-2 text-cyan-300" />
                        Console admin
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem
                    data-testid="nav-logout-button"
                    onSelect={handleLogout}
                    className="cursor-pointer px-3 py-2.5 text-pink-300 focus:bg-pink-500/15 focus:text-pink-200"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/login"
                data-testid="nav-login-link"
                className="px-3 sm:px-4 py-2 rounded-full border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 transition-all text-[11px] sm:text-sm"
              >
                Connexion
              </Link>
            )}
          </nav>
        </div>
      </header>
      <main>{children}</main>
      <LegalFooter />
      <InstallPwaBanner />
    </div>
  );
}
